import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { SubmitHandler, useForm } from 'react-hook-form';
import { format } from 'date-fns';
import { selectPatient, updateDemographics } from '../../../redux/assessment';
import { useAppDispatch } from '../../../pages/assessment';
import { selectIsGuardian, selectReferralSource } from '../../../redux/session';
import { useLDFlags } from '../../providers/ld-flags';
import { DemographicsBasics } from './demographics-basics';
import { DemographicsAddress } from './demographics-address';
import { DemographicsDiscovery } from './demographics-discovery';
import { AssessmentButtons, AssessmentHeader } from '../shared';
import { selectPresentMeta } from '../../../redux/meta';
import { TITLE_COLORS } from '../shared/redux-flow-container';

export interface DemographicFormValues {
  firstName: string;
  middleName: string;
  lastName: string;
  addressStreet1: string;
  addressStreet2: string;
  addressZipCode: string;
  addressCity: string;
  addressState: string;
  birthDate: string;
  phone: string;
  sex: string;
  discoveryChannel: string;
  discoveryChannelOther: string;
}

const DemographicsFormOld = () => {
  const { showSexField } = useLDFlags();
  const isGuardian = useSelector(selectIsGuardian);
  const { addToast } = useToasts();
  const dispatch = useAppDispatch();
  const patient = useSelector(selectPatient);
  const isReferral = !!useSelector(selectReferralSource);

  const router = useRouter();
  const { utm_source } = router.query;
  const wasReferredByZocdoc =
    typeof utm_source === 'string' && utm_source.toLowerCase() === 'zocdoc';

  const title = isGuardian
    ? showSexField
      ? 'Tell us about the patient'
      : 'Patient information'
    : 'Tell us about yourself';

  const subtitle = showSexField
    ? 'For billing purposes, we ask that what you enter below matches what your insurer has on file.'
    : isGuardian
    ? 'Please enter the patient’s information using this secure form.'
    : 'This will help us match you with the right psychiatrist.';

  const form = useForm<DemographicFormValues>({
    mode: 'onBlur',
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    watch,
  } = form;

  const onSubmit: SubmitHandler<DemographicFormValues> = async data => {
    try {
      const formattedBirthDate = format(new Date(data.birthDate), 'yyyy-MM-dd');
      const formattedPhone = data.phone?.replace(/[^0-9]/g, '');
      const formattedDiscoveryChannel =
        data.discoveryChannel === 'Other' && data.discoveryChannelOther
          ? `Other: ${data.discoveryChannelOther}`
          : data.discoveryChannel;

      const dataToSend = {
        firstName: data.firstName,
        middleName: data.middleName,
        lastName: data.lastName,
        addressStreet1: data.addressStreet1,
        addressStreet2: data.addressStreet2,
        addressZipCode: data.addressZipCode,
        addressCity: data.addressCity,
        addressState: data.addressState,
        birthDate: formattedBirthDate,
        sex: data.sex,
        wasReferredByZocdoc,
        ...(!isGuardian && {
          phone: formattedPhone,
          discoveryChannel: formattedDiscoveryChannel,
        }),
      };
      await dispatch(updateDemographics(dataToSend));
    } catch (e) {
      addToast(
        'Sorry, something went wrong. Please refresh the page and try again.',
        { appearance: 'error' }
      );
    }
  };

  return (
    <>
      <AssessmentHeader title={title} subtitle={subtitle} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <DemographicsBasics
          form={form}
          patient={patient}
          isGuardian={isGuardian}
          isReferral={isReferral}
          showSexField={showSexField}
        />
        <DemographicsAddress
          control={control}
          patient={patient}
          showSexField={showSexField}
        />
        <DemographicsDiscovery
          control={control}
          patient={patient}
          isVisible={!isGuardian}
          showOtherField={watch('discoveryChannel') === 'Other'}
        />
        <AssessmentButtons
          primary={{
            children: 'Continue',
            type: 'submit',
            disabled: isSubmitting,
            loading: isSubmitting,
          }}
        />
      </form>
    </>
  );
};

const DemographicsForm2Point1 = () => {
  const { showSexField } = useLDFlags();
  const isGuardian = useSelector(selectIsGuardian);
  const { addToast } = useToasts();
  const dispatch = useAppDispatch();
  const patient = useSelector(selectPatient);
  const isReferral = !!useSelector(selectReferralSource);
  const meta = useSelector(selectPresentMeta);

  const router = useRouter();
  const { utm_source } = router.query;
  const wasReferredByZocdoc =
    typeof utm_source === 'string' && utm_source.toLowerCase() === 'zocdoc';

  const title = isGuardian
    ? `Finish ${patient.preferredName}'s profile`
    : `Finish your profile, ${patient.preferredName}`;

  const subTitle = 'We just need a few more details.';
  const headerText = isGuardian
    ? `For billing purposes, we ask that what you enter here matches what ${patient.preferredName}'s insurance has on file.`
    : 'For billing purposes, we ask that what you enter here matches what your insurance has on file.';

  const form = useForm<DemographicFormValues>({
    mode: 'onBlur',
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = form;

  const onSubmit: SubmitHandler<DemographicFormValues> = async data => {
    try {
      const formattedBirthDate = format(new Date(data.birthDate), 'yyyy-MM-dd');
      const formattedPhone = data.phone?.replace(/[^0-9]/g, '');
      const formattedDiscoveryChannel =
        data.discoveryChannel === 'Other' && data.discoveryChannelOther
          ? `Other: ${data.discoveryChannelOther}`
          : data.discoveryChannel;

      const dataToSend = {
        firstName: data.firstName,
        middleName: data.middleName,
        lastName: data.lastName,
        addressStreet1: data.addressStreet1,
        addressStreet2: data.addressStreet2,
        addressZipCode: data.addressZipCode,
        addressCity: data.addressCity,
        addressState: data.addressState,
        birthDate: formattedBirthDate,
        sex: data.sex,
        wasReferredByZocdoc,
        ...(!isGuardian && {
          phone: formattedPhone,
          discoveryChannel: formattedDiscoveryChannel,
        }),
      };
      await dispatch(updateDemographics(dataToSend));
    } catch (e) {
      addToast(
        'Sorry, something went wrong. Please refresh the page and try again.',
        { appearance: 'error' }
      );
    }
  };

  return (
    <>
      <AssessmentHeader
        title={title}
        titleColor={TITLE_COLORS[meta.assessmentSection]}
        boldedSubtitle={subTitle}
        text={headerText}
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <DemographicsBasics
          form={form}
          patient={patient}
          isGuardian={isGuardian}
          isReferral={isReferral}
          showSexField={showSexField}
        />
        <DemographicsAddress
          control={control}
          patient={patient}
          showSexField={showSexField}
        />
        <AssessmentButtons
          primary={{
            children: isGuardian
              ? 'Sign consent for minor'
              : "Tell us how you're feeling",
            type: 'submit',
            disabled: isSubmitting,
            loading: isSubmitting,
          }}
        />
      </form>
    </>
  );
};

const DemographicsForm = () => {
  const { shouldUseIntakeV2Point1 } = useLDFlags();
  return shouldUseIntakeV2Point1 ? (
    <DemographicsForm2Point1 />
  ) : (
    <DemographicsFormOld />
  );
};
export default DemographicsForm;
